import { ReactElement, ReactNode } from 'react';
import { BLOCKS, INLINES, Node } from '@contentful/rich-text-types';
import { exposeClassnames as classnames } from '@stadionhq-v3/utilities';
import {
  Heading,
  Paragraph,
  RteTable,
  RteTableCell,
  RteTableHeadCell,
  RteTableRow,
} from '@stadionhq-v3/base-ui';
import { Theme } from '@stadionhq-v3/core';
import styles from './useContentfulRichTextRendererOptions.module.scss';

export const appendClassNamesToReactElements = (
  nodes: ReactElement[],
  additionalClassNames: string[]
) =>
  nodes?.map((child) => ({
    ...child,
    props: {
      ...child.props,
      className: classnames([
        ...(child.props.className?.split(' ') || []),
        additionalClassNames,
      ]),
    },
  }));

export const renderCommonElements = (theme?: Theme, onDark?: boolean) => ({
  [BLOCKS.HEADING_2]: (_node: Node, children: ReactNode) => (
    <Heading as="h2" classNames={[classnames(styles.h2)]}>
      {children}
    </Heading>
  ),
  [BLOCKS.HEADING_3]: (_node: Node, children: ReactNode) => (
    <Heading as="h3" classNames={[classnames(styles.h3)]}>
      {children}
    </Heading>
  ),
  [BLOCKS.HEADING_4]: (_node: Node, children: ReactNode) => (
    <Heading as="h4" classNames={[classnames(styles.h4)]}>
      {children}
    </Heading>
  ),
  [BLOCKS.PARAGRAPH]: (_node: Node, children: ReactNode) => (
    <Paragraph classNames={[classnames(styles.paragraph)]}>
      {children}
    </Paragraph>
  ),
  [BLOCKS.UL_LIST]: (_node: Node, children: ReactNode) => (
    <ul className={styles.list}>
      {appendClassNamesToReactElements(children as ReactElement[], [
        classnames(styles.unorderedlistItem),
      ])}
    </ul>
  ),
  [BLOCKS.OL_LIST]: (_node: Node, children: ReactNode) => (
    <ol className={classnames([styles.list, styles.orderedListItem])}>
      {appendClassNamesToReactElements(children as ReactElement[], [
        classnames(styles.orderedListItem),
      ])}
    </ol>
  ),
  [BLOCKS.LIST_ITEM]: (_node: Node, children: ReactNode) => <li>{children}</li>,
  [BLOCKS.TABLE]: (_node: Node, children: ReactNode) => (
    <section className={styles.tableContainer}>
      <RteTable
        theme={theme}
        onDark={onDark}
        nativeProps={{ style: { width: '100%' } }}
      >
        <tbody>{children}</tbody>
      </RteTable>
    </section>
  ),
  [BLOCKS.TABLE_ROW]: (_node: Node, children: ReactNode) => (
    <RteTableRow onDark={onDark} theme={theme}>
      {children}
    </RteTableRow>
  ),
  [BLOCKS.TABLE_HEADER_CELL]: (_node: Node, children: ReactNode) => (
    <RteTableHeadCell
      classNames={[classnames(styles.tableHeadCell)]}
      theme={theme}
      onDark={onDark}
    >
      {children}
    </RteTableHeadCell>
  ),
  [BLOCKS.TABLE_CELL]: (_node: Node, children: ReactNode) => (
    <RteTableCell
      classNames={[classnames(styles.tableCell)]}
      theme={theme}
      onDark={onDark}
    >
      {children}
    </RteTableCell>
  ),
  [BLOCKS.HR]: () => <hr className={styles.hr} />,
  [INLINES.HYPERLINK]: (node: Node, children: ReactNode) => {
    const { data } = node;
    if (children) {
      return (
        <a className={styles.link} href={data.uri}>
          {children}
        </a>
      );
    }
    return <></>;
  },
});
